import { useEffect, useRef, type JSX } from 'react';

import { cn } from '@/lib/utils.js';
import { formatTimecode } from '../quality-presentation.js';

import { chapterLabel, type Chapter } from './chapters.js';
import { PLAYER_DOCK_ID } from './player-dock-id.js';

/** A chapter whose gap to the next one (or, for the last chapter, its own duration) is at least
 *  this long shows that duration explicitly (AC 21) — real data has a sync step that sits still for
 *  40 of a 48-minute recording, and without the label the highlight reads as a frozen UI. */
const LONG_CHAPTER_MS = 60_000;

/** "+40m" — chapters only ever need whole-minute precision; a step lasting that long doesn't need
 *  seconds. */
function formatLongDuration(durationMs: number): string {
  return `+${Math.round(durationMs / 60_000)}m`;
}

/**
 * Whichever element actually scrolls the chapter rows: the list itself (it carries its own `max-h` +
 * `overflow-y-auto`), or the dock card around it when the card became the scroller instead. Writing
 * `scrollTop` on a list that cannot scroll is a silent no-op — the active chapter would simply stop
 * following the playhead, with nothing to see in a test that renders without CSS.
 */
function scrollableAncestor(
  element: HTMLElement | null,
  boundaryId: string,
): HTMLElement | null {
  for (let node = element; node; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    const scrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (scrollable && node.scrollHeight > node.clientHeight) return node;
    /** Never walk past the dock: the next scrollable ancestor out there is the PAGE, and scrolling
     *  the page to follow a chapter is exactly what a timecode click must never do (AC 8). */
    if (node.id === boundaryId) break;
  }
  return element;
}

/** Same status→color mapping as `CasesReport`'s step rows (kept local — a five-line pure mapping
 *  isn't worth importing across a file boundary another step owns). */
function statusDotClass(status: string): string {
  if (status === 'passed') return 'bg-success-500';
  if (status === 'timeout') return 'bg-warning-500';
  if (status === 'failed' || status === 'broken') return 'bg-destructive';
  if (status === 'running' || status === 'in_progress') return 'bg-info-500';
  return 'bg-muted-foreground/40';
}

/**
 * The bound case's steps, rendered under the docked player (AC 18). `activeIndex` (-1 = none) drives
 * the highlight; `PlayerDock` computes it from `timeupdate` via `activeChapterIndex` and only passes
 * a new value when it actually changes. Clicking a chapter seeks (AC 20). Auto-scrolling the active
 * row into view suspends after the user's own wheel/touch/arrow-key interaction with the list (AC
 * 22) — never on `scroll`, since a programmatic `scrollIntoView` fires that too and would self-suspend
 * on the very first update. The suspend follows the same case/attempt scope as everything else
 * `PlayerDock` resets on a rebind (see its `chapterScopeCaseId`/`chapterScopeAttemptNumber` effect):
 * `ChapterList` stays mounted across rebinds, so without this reset one gesture on case A would leave
 * auto-scroll off for every case bound afterwards (Copilot round 5).
 */
export function ChapterList({
  chapters,
  activeIndex,
  onSelect,
  chapterScopeCaseId = null,
  chapterScopeAttemptNumber = null,
}: {
  chapters: Chapter[];
  activeIndex: number;
  onSelect: (chapter: Chapter) => void;
  chapterScopeCaseId?: string | null;
  chapterScopeAttemptNumber?: number | null;
}): JSX.Element | null {
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeRowRef = useRef<HTMLButtonElement | null>(null);
  const autoScrollSuspended = useRef(false);

  /** A rebind to a different case/attempt scope re-arms auto-scroll — the flag is a per-scope
   *  concern, not a permanent one, even though `ChapterList` itself never unmounts across rebinds. */
  useEffect(() => {
    autoScrollSuspended.current = false;
  }, [chapterScopeCaseId, chapterScopeAttemptNumber]);

  /** The suspend switch: any of these user gestures turns auto-scroll off until the next scope
   *  change. Bound to the element that ACTUALLY scrolls as well as to the list itself — when the dock
   *  card is the scroller, a wheel or touch gesture over the video or the header moves the rows
   *  without ever reaching the list, and follow would keep yanking the panel back under the user. */
  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    /** The dock card, not "whatever scrolls right now": whether the card scrolls depends on how much
     *  has loaded, and a listener attached at mount would miss the gesture that matters later. */
    /** `closest<HTMLElement>`: the untyped overload returns `Element`, whose event map has no
     *  `keydown`, so a `KeyboardEvent` listener no longer type-checks against it — `tsc -b` catches
     *  that even where `tsc --noEmit` on the app project alone does not. */
    const card = list.closest<HTMLElement>(`#${PLAYER_DOCK_ID}`);
    const targets: HTMLElement[] =
      card && card !== list ? [list, card] : [list];
    const suspend = (): void => {
      autoScrollSuspended.current = true;
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key.startsWith('Arrow')) suspend();
    };
    for (const target of targets) {
      target.addEventListener('wheel', suspend, { passive: true });
      target.addEventListener('touchmove', suspend, { passive: true });
      target.addEventListener('keydown', onKeyDown);
    }
    return () => {
      for (const target of targets) {
        target.removeEventListener('wheel', suspend);
        target.removeEventListener('touchmove', suspend);
        target.removeEventListener('keydown', onKeyDown);
      }
    };
    /** Re-runs when the list appears or disappears, not once at mount: this component stays mounted
     *  across rebinds and renders nothing while the bound case has no timed steps, so a bind to a
     *  case that DOES have them would otherwise leave the list on screen with no listeners — and
     *  auto-scroll would keep yanking it back under a user who is scrolling by hand. */
  }, [chapters.length === 0]);

  /** Scrolls the LIST'S OWN scrollbar only — never `scrollIntoView`, which walks up to the document
   *  and can drag the whole page along with it in `wide` mode (the list sits in normal document flow
   *  there, not inside its own scroll container's clipped viewport).
   *
   * The row's position is computed from `getBoundingClientRect()` deltas against the list, NOT
   * `row.offsetTop` — `offsetTop` is measured from the nearest POSITIONED ancestor, which in drawer
   * mode is `PlayerDock`'s `fixed` card (header + video), not this scrollable list, since the list
   * itself is never given `position: relative`. A `getBoundingClientRect` delta is immune to that:
   * it reads the actual rendered geometry regardless of which ancestor happens to be positioned, so
   * it can't silently break again if a future style change interposes another positioned element
   * between the list and its rows (making the list itself the containing block would only fix
   * TODAY's ancestor, not the next one). */
  useEffect(() => {
    if (autoScrollSuspended.current) return;
    const row = activeRowRef.current;
    const scroller = scrollableAncestor(listRef.current, PLAYER_DOCK_ID);
    if (!scroller || !row) return;
    const rowTop =
      row.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (rowTop < scroller.scrollTop) {
      scroller.scrollTop = rowTop;
    } else if (rowBottom > scroller.scrollTop + scroller.clientHeight) {
      scroller.scrollTop = rowBottom - scroller.clientHeight;
    }
  }, [activeIndex]);

  if (chapters.length === 0) return null;

  return (
    <div
      ref={listRef}
      role="list"
      aria-label="Chapters"
      className="flex max-h-[32vh] min-h-0 min-w-0 flex-col gap-0.5 overflow-y-auto"
    >
      {chapters.map((chapter, index) => {
        const active = index === activeIndex;
        return (
          // `role="listitem"` lives on this wrapper, not the button — a button carrying `listitem`
          // loses its own button semantics to assistive tech (Copilot round 5). The button inside
          // stays a real, focusable, keyboard-activatable control.
          <div
            key={`${index}-${chapter.timecodeMs}-${chapter.name}`}
            role="listitem"
          >
            <button
              ref={active ? activeRowRef : undefined}
              type="button"
              aria-label={`${chapter.name || 'step'} — ${formatTimecode(chapter.timecodeMs)}`}
              aria-current={active ? 'true' : undefined}
              onClick={() => onSelect(chapter)}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs transition-colors',
                active
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent/50',
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    'size-1.5 shrink-0 rounded-full',
                    statusDotClass(chapter.status),
                  )}
                />
                <span className="truncate">
                  {chapterLabel(chapter.name) || 'step'}
                </span>
              </span>
              <span className="shrink-0 whitespace-nowrap font-mono text-[11px] text-muted-foreground/80">
                {formatTimecode(chapter.timecodeMs)}
                {chapter.durationMs !== null &&
                chapter.durationMs >= LONG_CHAPTER_MS
                  ? ` · ${formatLongDuration(chapter.durationMs)}`
                  : null}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
